// common/hooks/useAuthentification.js
import { useEffect, useState, useCallback } from "react";
import { LOCAL_STORAGE_KEYS } from "../constants";
import { UserService } from "../services";

// Rôles ayant accès complet
const FULL_ACCESS_ROLES = ['superAdmin', 'directeur', 'support'];

// Configuration des routes avec leurs permissions (ordre de priorité)
const ROUTE_PERMISSIONS = [
    { path: '/dashboard', perm: 'dashboard:view', label: 'Tableau de bord' },
    { path: '/dashboard/banners', perm: 'banners:read', label: 'Bannières Website' },
    { path: '/dashboard/vehicle-brands', perm: 'vehicleBrands:read', label: 'Marques & Modèles' },
    { path: '/dashboard/categories', perm: 'categories:read', label: 'Gestion Services' },
    { path: '/dashboard/partenaires', perm: 'partners:read', label: 'Partenaires' },
    { path: '/dashboard/vehicules', perm: 'vehicles:read', label: 'Véhicules' },
    { path: '/dashboard/chauffeurs', perm: 'drivers:read', label: 'Chauffeurs' },
    { path: '/dashboard/historique-numeros', perm: 'userPhone.history:read', label: 'Historique Numéros' },
    { path: '/dashboard/historique-transferts', perm: 'transfers.history:read', label: 'Historique Transferts' },
    { path: '/dashboard/demandes-assistances', anyOf: ['assistance:read', 'assistance:update'], label: 'Demandes Assistance' },
    { path: '/dashboard/gestion-admins', perm: 'users:read', label: 'Administrateurs' },
    { path: '/dashboard/roles', perm: 'roles:read', label: 'Rôles' },
    { path: '/dashboard/wallet', anyOf: ['finance:read', 'finance:update'], label: 'Wallet' },
    { path: '/dashboard/historique-promotions', perm: 'promotions:read', label: 'Promotions' },
    { path: '/dashboard/sites', perm: 'sites:read', label: 'Sites Terrain' },
    { path: '/dashboard/settings', perm: 'settings:read', label: 'Paramètres' },
    { path: '/dashboard/sync-chauffeurs', perm: 'sync:read', label: 'Synchronisation' },
];

// Fonction pour trouver la première page accessible
const getFirstAccessiblePage = (user) => {
    if (!user) return '/dashboard';

    // Si l'utilisateur a un rôle avec accès complet
    if (FULL_ACCESS_ROLES.includes(user.role)) {
        return '/dashboard';
    }

    const perms = new Set(user.permissions || []);

    for (const route of ROUTE_PERMISSIONS) {
        if (route.anyOf) {
            if (route.anyOf.some(p => perms.has(p))) {
                return route.path;
            }
        } else if (route.perm) {
            if (perms.has(route.perm)) {
                return route.path;
            }
        }
    }

    // Par défaut, rediriger vers le dashboard (qui montrera la page Access Denied)
    return '/dashboard';
};

export default function UseAuthentification() {
    const [currentUser, setCurrentUser] = useState(() => {
        const raw = localStorage.getItem(LOCAL_STORAGE_KEYS.CURRENT_USER);
        return raw ? JSON.parse(raw) : null;
    });
    const [initialized, setInitialized] = useState(false);

    const onLogin = useCallback((loginResp, navigate) => {
        const token = loginResp?.accessToken;
        if (token) {
            localStorage.setItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, token);
        }
        // selon ton backend, soit tu as "user" retourné, soit tu rappelles /users/me :
        const user = loginResp?.data || loginResp; // adapte si besoin
        localStorage.setItem(LOCAL_STORAGE_KEYS.CURRENT_USER, JSON.stringify(user));
        setCurrentUser(user);
        setInitialized(true);

        // Rediriger vers la première page accessible
        const firstPage = getFirstAccessiblePage(user);
        navigate(firstPage);
    }, []);

    const onLogout = useCallback(() => {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(LOCAL_STORAGE_KEYS.CURRENT_USER);
        setCurrentUser(null);
        setInitialized(true);
    }, []);

    useEffect(() => {
        // réhydratation au F5
        const token = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
        const hasUser = !!localStorage.getItem(LOCAL_STORAGE_KEYS.CURRENT_USER);
        if (!token) {
            setInitialized(true);
            return;
        }
        if (hasUser) {
            setInitialized(true);
            return;
        }
        // token présent mais pas d'user en cache -> on récupère /users/me
        (async () => {
            try {
                const me = await UserService.getUserInfo();
                localStorage.setItem(LOCAL_STORAGE_KEYS.CURRENT_USER, JSON.stringify(me));
                setCurrentUser(me);
            } catch {
                // si 401, exeRequest redirigera; on se met tout de même "ready"
            } finally {
                setInitialized(true);
            }
        })();
    }, []);

    const isAuthenticated = !!localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);

    // Exposer la fonction pour utilisation externe
    const getFirstPage = useCallback(() => {
        return getFirstAccessiblePage(currentUser);
    }, [currentUser]);

    return { currentUser, setCurrentUser, onLogin, onLogout, isAuthenticated, initialized, getFirstPage };
}
